import { useCallback, useState } from 'react';
import { resolveApiErrorMessage } from '@sorrem-client/ui/store';

export interface MutationErrorState {
    /** Why the last request failed (or could not start), or null. */
    error: string | null;
    /** Shows why a request cannot start (signed out, say). Resolves to false, as a failed `run` does. */
    refuse: (message: string) => Promise<false>;
    /**
     * Clears the error, sends `request`, and resolves to whether it went
     * through. On a failure the error becomes the server's reason, else
     * `fallback`; a function maps the failure itself (a code with its own text).
     */
    run: (request: () => Promise<unknown>, fallback: string | ((failure: unknown) => string)) => Promise<boolean>;
}

/**
 * The error half of a mutation hook: one place for the clear-send-unwrap
 * dance and the player-facing reason a request failed.
 */
export function useMutationError(): MutationErrorState {
    const [error, setError] = useState<string | null>(null);

    const refuse = useCallback(async (message: string): Promise<false> => {
        setError(message);
        return false;
    }, []);

    const run = useCallback(async (request: () => Promise<unknown>, fallback: string | ((failure: unknown) => string)): Promise<boolean> => {
        setError(null);
        try {
            await request();
            return true;
        } catch (failure) {
            setError(typeof fallback === 'string' ? resolveApiErrorMessage(failure, fallback) : fallback(failure));
            return false;
        }
    }, []);

    return { error, refuse, run };
}
